import type { ApiSchema } from './client'
import type { ScheduledGreetingInternal } from '~/utils/scheduleHelpers'
import { scheduledGreetingsFromApi } from '~/utils/scheduleHelpers'

export interface CustomerPlan {
  agentQualityCallAnalysisAvailable: boolean
  agentQualityConfigurationAvailable: boolean
  agentVoiceChoosable: boolean
  baseAccess: boolean
  canSetPersona: boolean
  callDataExportAvailable: boolean
  crmCsvDataImport: boolean
  freeMinsPerMonth: number
  fullCallTranscriptVisible: boolean
  integrationAirtableAvailable: boolean
  integrationCrmCatchXmlFileAvailable: boolean
  integrationPbxAvailable: boolean
  integrationTanssAvailable: boolean
  integrationZammadAvailable: boolean
  integrationsAvailable: boolean
  multilingualAgents: boolean
  numberOfMultilingualAgents: number
  numberOfAgents: number
  outboundCampaignsAvailable: boolean
  priceMonthly: number
  pricePerMin: number
  priceYearly: number
  teamCallManagementAvailable: boolean
  telephonyByoSipAvailable: boolean
  voicesGemini: boolean
  voicesMistral: boolean
}

export interface CustomerTeamWorkflowSettings {
  callCommentsEditable: boolean
  callerNumberDialFormat: ApiSchema<'CallerNumberDialFormat'>
}

export interface Agent {
  active: boolean
  activeVoice?: string | null
  availabilitySchedule?: ApiSchema<'GetAgent'>['availability_schedule']
  availabilityScheduleEnabled: boolean
  customerId: string
  directions: string
  generalCallNotificationEmails: ApiSchema<'NotificationEmailAddress-Output'>[]
  generalCallNotificationEmailTemplateId?: string | null
  groupedCallNotificationEmailTemplateId?: string | null
  callNotificationGrouping?: ApiSchema<'CallNotificationGrouping'> | null
  notifyOnForwardedCalls?: boolean | null
  sendActionEmailsDuringForwardedCall?: boolean | null
  greeting: string
  greetingPlaybackSpeed: number
  id: string
  internalLabel?: string | null
  name: string
  organizationDescription: string
  organizationName: string
  outsideHoursBehavior?: ApiSchema<'GetAgent'>['outside_hours_behavior']
  outsideHoursForwardNumber?: string | null
  outsideHoursMessage?: string | null
  persona: string
  phoneNumber?: string | null
  expectedLanguages: string[]
  multilingualEnabled: boolean
  role: string
  scheduledGreetings: ScheduledGreetingInternal[]
  segmentationSilenceTimeoutInMs: number
  task: string
  timezone?: string | null
  voices: ApiSchema<'GetAgent'>['voices']
}

export const mapCustomerPlan = (plan: ApiSchema<'GetCustomerPlan'>): CustomerPlan => ({
  agentQualityCallAnalysisAvailable: plan.agent_quality_call_analysis_available ?? false,
  agentQualityConfigurationAvailable: plan.agent_quality_configuration_available ?? false,
  agentVoiceChoosable: plan.agent_voice_choosable,
  baseAccess: plan.base_access,
  canSetPersona: plan.can_set_persona,
  callDataExportAvailable: plan.call_data_export_available,
  crmCsvDataImport: plan.crm_csv_data_import,
  freeMinsPerMonth: plan.free_mins_per_month,
  fullCallTranscriptVisible: plan.full_call_transcript_visible,
  integrationAirtableAvailable: plan.integration_airtable_available ?? false,
  integrationCrmCatchXmlFileAvailable: plan.integration_crm_catch_xml_file_available,
  integrationPbxAvailable: plan.integration_pbx_available ?? false,
  integrationTanssAvailable: plan.integration_tanss_available,
  integrationZammadAvailable: plan.integration_zammad_available,
  integrationsAvailable: plan.integrations_available,
  multilingualAgents: plan.multilingual_agents,
  numberOfMultilingualAgents: plan.number_of_multilingual_agents,
  numberOfAgents: plan.number_of_agents,
  outboundCampaignsAvailable: plan.outbound_campaigns_available,
  priceMonthly: plan.price_monthly,
  pricePerMin: plan.price_per_min,
  priceYearly: plan.price_yearly,
  teamCallManagementAvailable: plan.team_call_management_available,
  telephonyByoSipAvailable: plan.telephony_byo_sip_available ?? false,
  voicesGemini: plan.voices_gemini,
  voicesMistral: plan.voices_mistral,
})

export const mapCustomerTeamWorkflowSettings = (
  settings: ApiSchema<'GetCustomerTeamWorkflowSettings'>,
): CustomerTeamWorkflowSettings => ({
  callCommentsEditable: settings.call_comments_editable,
  callerNumberDialFormat: settings.caller_number_dial_format ?? 'e164_plus',
})

export const mapAgent = (agent: ApiSchema<'GetAgent'>): Agent => ({
  active: agent.active,
  activeVoice: agent.active_voice,
  availabilitySchedule: agent.availability_schedule,
  availabilityScheduleEnabled: agent.availability_schedule_enabled,
  customerId: agent.customer_id,
  directions: agent.directions,
  generalCallNotificationEmails: (agent.general_call_notification_emails ?? []).map(
    entry => ({
      email: entry.email,
      active: entry.active ?? true,
      schedule: entry.schedule ?? null,
    }),
  ),
  generalCallNotificationEmailTemplateId:
    agent.general_call_notification_email_template_id ?? null,
  groupedCallNotificationEmailTemplateId:
    agent.grouped_call_notification_email_template_id ?? null,
  callNotificationGrouping: agent.call_notification_grouping ?? null,
  notifyOnForwardedCalls: agent.notify_on_forwarded_calls ?? null,
  sendActionEmailsDuringForwardedCall:
    agent.send_action_emails_during_forwarded_call ?? null,
  greeting: agent.greeting,
  greetingPlaybackSpeed: agent.greeting_playback_speed,
  id: agent.id,
  internalLabel: agent.internal_label,
  name: agent.name,
  organizationDescription: agent.organization_description,
  organizationName: agent.organization_name,
  outsideHoursBehavior: agent.outside_hours_behavior,
  outsideHoursForwardNumber: agent.outside_hours_forward_number,
  outsideHoursMessage: agent.outside_hours_message,
  persona: agent.persona,
  phoneNumber: agent.phone_number,
  expectedLanguages: agent.expected_languages ?? [],
  multilingualEnabled: agent.multilingual_enabled,
  role: agent.role,
  scheduledGreetings: scheduledGreetingsFromApi(agent.scheduled_greetings),
  segmentationSilenceTimeoutInMs: agent.segmentation_silence_timeout_in_ms,
  task: agent.task,
  timezone: agent.timezone,
  voices: agent.voices,
})
